<template>
  <div class="aboutAuthenticatorsContainer w-100">
    <div class="container">
      <div class="row overflow">
        <div class="col pt-3">
          <h3 class="text-center mb-4 mt-10">AUTHENTICATION AND GRADING PARTNERSHIPS</h3>
          <p class="mb-5 text-corduroy" :class="isMobile ? 'text-left' : 'text-center'">
            Quick, easy, stress-free paths for authenticating raw trading cards.
          </p>
          <AuthenticatorsTable v-if="!isMobile" />
          <PartnershipMobileCards v-else />
          <div class="flex-col-center py-3" :class="isMobile ? 'pb-4' : ' my-10'">
            <a class="btn btn-primary btn-lg mb-4" :href="submitUrl">Submit Now</a>
            <p class="text-center mb-0 text-corduroy">
              <b class="text-dark">1% fulfillment fee</b> applies to any asset which is fulfilled or transferred out of the Fanatics Vault.
              <br v-if="!isMobile"/>
              <b class="text-dark">This fee is increased to 3%</b> if the asset is removed within 90 days of archival.
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="bg-dark">
      <div class="container">
        <div class="row">
          <div class="col" :class="isMobile ? 'pt-10' : 'pt-20 pb-10'">
            <h3 class="text-center text-white">How it works</h3>
            <p class="text-center text-white mb-0">Raw, Authenticated, Archived, and on the market.</p>
            <HowItWorksStepper v-if="!isMobile" />
            <HowItWorksStepperMobile v-else />
          </div>
        </div>
      </div>
    </div>
    <div class="text-corduroy bg-lighter">
      <div class="container py-5">
        <div class="row">
          <div class="col-12 col-md-6">
            <AspectWrapper :aspectRatio="520 / 675">
              <img alt="about cgc" class="h-100 w-100 contain" :class="isMobile ? 'rounded-top-left' : 'rounded-medium'" :src="cgcImageSrc">
            </AspectWrapper>
          </div>
          <div class="col-12 col-md-6">
            <div class="flex-col-start-center h-100">
              <span class="text-dark font-size-12 font-weight-bold my-2">About</span>
              <div class="flex-start-center">
                <Cgc fill="#000" width="130px" />
              </div>
              <p class="mt-4">Certified Collectibles Group is a leading provider of expert, impartial, and tech-enabled services for collectibles. CGC has graded more than 70 million collectibles since 1987.</p>
              <p>CGC Cards has no value-based grading tiers or submission volume limits. Service includes manufacturer-issued autograph grades.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="container py-5 text-corduroy">
      <div class="row" :class="{'flex-col-reverse': isMobile}">
        <div class="col-12 col-md-6">
          <div class="flex-col-start-center h-100">
            <span class="text-dark font-size-12 font-weight-bold my-2">About</span>
            <Sgc fill="#000" height="36px" width="92px" />
            <p class="mt-4">SGC has roots as the original trading card authentication service dating back to the early '90s. Historically specializing in vintage trading card authentication -- including turn of the century candy and tobacco issues -- SGC is now widely recognized as a leader in all genres thanks to its quality control.</p>
            <p>SGC has no value-based grading tiers and no submission volume limits. Service includes manufacturer-issued autograph grades.</p>
          </div>
        </div>
        <div class="col-12 col-md-6">
          <AspectWrapper :aspectRatio="520 / 675">
            <img alt="about sgc" class="h-100 w-100 contain" :class="isMobile ? 'rounded-top-left' : 'rounded-medium'" :src="sgcImageSrc">
          </AspectWrapper>
        </div>
      </div>
    </div>
    <div class="bg-light text-corduroy">
      <div class="container py-5 text-corduroy">
        <div class="row">
          <div class="col-12 col-md-6">
            <AspectWrapper :aspectRatio="520 / 675">
              <img alt="about beckett" class="h-100 w-100 contain" :class="isMobile ? 'rounded-top-left' : 'rounded-medium'" :src="beckettImageSrc">
            </AspectWrapper>
          </div>
          <div class="col-12 col-md-6">
            <div class="flex-col-start-center h-100">
              <span class="text-dark font-size-12 font-weight-bold my-2">About</span>
              <Beckett fill="#000" height="35px" width="135px" />
              <p class="mt-4">Beckett is a trusted name dating back to its days as the original price guide provider. The company's grading services were the first to introduce the concept of subgrades and its services are held in high regard.</p>
              <p>The mission of Beckett Collectibles is to provide the most amazing products and services on the planet for collectors.</p>
              <p>Beckett has no value-based grading tiers and no submission volume limits. Service includes subgrades and manufacturer-issued autograph grades.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="text-corduroy">
      <div class="container py-5 text-corduroy">
        <div class="row">
          <div class="col-12 col-md-6">
            <div class="flex-col-start-center h-100">
              <span class="text-dark font-size-12 font-weight-bold my-2">About</span>
              <PSALogo fill="#000" fillS="#000" height="40px" width="110px" />
              <p class="mt-4">Considered by many to be the industry standard for authentication and grading, PSA has assessed more than 65 million trading cards and pieces of memorabilia globally since its establishment in 1991.</p>
              <p>A range of grading tiers gives collectors the flexibility to submit based on volume, value, schedule, and more, with the option to include autograph grading as well.</p>
            </div>
          </div>
          <div class="col-12 col-md-6">
            <AspectWrapper :aspectRatio="520 / 675">
              <img alt="about psa" class="h-100 w-100 contain" :class="isMobile ? 'rounded-top-left' : 'rounded-medium'" :src="psaImageSrc">
            </AspectWrapper>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { baseIsMobile } from '@js/www/marketplaces/mixins';
import AspectWrapper from '@js/web/components/misc/AspectWrapper';
import AuthenticatorsTable from './AuthenticatorsTable';
import HowItWorksStepper from './HowItWorksStepper';
import HowItWorksStepperMobile from './HowItWorksStepperMobile';
import PartnershipMobileCards from './PartnershipMobileCards';
import { Beckett, Cgc, Mba, PSALogo, Sgc } from '@js/web/components/icons';

export default {
  name: 'AboutAuthenticators',
  components: {
    AspectWrapper,
    AuthenticatorsTable,
    Beckett,
    Cgc,
    HowItWorksStepper,
    HowItWorksStepperMobile,
    PartnershipMobileCards,
    PSALogo,
    Sgc,
  },
  mixins: [ baseIsMobile ],
  data() {
    return {
      imagesUrl: jsConfig.links?.imagesUrl,
      submitUrl: jsConfig.links?.submitUrl,
    };
  },
  computed: {
    beckettImageSrc() {
      return this.imagesUrl + 'partnerships-Beckett.png';
    },
    cgcImageSrc() {
      return this.imagesUrl + 'partnerships-CSG_CGC.png';
    },
    sgcImageSrc() {
      return this.imagesUrl + 'partnerships-SGC.png';
    },
    psaImageSrc() {
      return this.imagesUrl + 'partnerships-PSA.webp';
    },
  }
};
</script>

<style lang="scss">
@import '@sass/vue-imports.scss';

.aboutAuthenticatorsContainer {
  @include media-breakpoint-down(sm) {
    h3 {
      font-size: 18px !important;
    }
  }
}
</style>
